import { ResultList as HeadlessResultList } from '@coveo/headless';
import React from 'react';
import { FunctionComponent, useEffect, useState } from 'react';
import { addEllipsis, getCurrencyInUSD } from '../../utils/helper';
import { Link } from 'react-router-dom';
import CheckCircle from '@mui/icons-material/CheckCircle';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import HistoryCircle from '@mui/icons-material/HistoryRounded';
import CartSlider from '../Cart/CartSlider/CartSlider';
import Button from '@mui/material/Button';
import nextId from 'react-id-generator';
import { InteractiveResult } from './interactive-result';
import RelatedPrograms, { RelatedProgramsFieldsProps } from '../RelatedPrograms/RelatedPrograms';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import { IPriceOptionFields } from '../PriceOptions/PriceOptions';
import { getFieldSpecificConfiguration } from '../../utils/fieldSpecificConfigHelper';
import { IFieldSpecificConfig } from '../../model/IFieldSpecificConfig';
import { ProductType } from '../../utils/ProductTypes';
import { ReportType } from '../../utils/ReportTypes';
import {
  STANDARDS_IDENTIFIER,
  TRAINING_IDENTIFIER,
  TRAINING_IDENTIFIER_COVEO,
} from '../../utils/constants';
import { translateProductType } from './facet';
import DOMPurify from 'dompurify';
import { formatTermDisplayValue } from '../../utils/StatisticsStoreHelper';

interface ResultListProps {
  controller: HeadlessResultList;
  Programs: Array<RelatedProgramsFieldsProps>;
  ProgramCount: Field<number>;
  countryConfigFields?: IPriceOptionFields;
  formatOptions?: any;
  fieldConfig: Array<IFieldSpecificConfig>;
}

export const ResultList: FunctionComponent<ResultListProps> = (props) => {
  const { controller } = props;
  const [state, setState] = useState(controller.state);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [productId, setProductId] = useState<string>('');
  const [productType, setProductType] = useState<string>('');
  const [productTitle, setProductTitle] = useState<string>('');
  const [edition, setEdition] = useState<string>('');
  const [expandMode, setExpandMode] = useState(false);
  const [term, setTerm] = useState('');
  const [year, setYear] = useState('');
  const [lob, setLob] = useState<string | null>(null);

  const [fieldSpecificConfig, setFieldSpecificConfig] = useState<IFieldSpecificConfig>();

  useEffect(() => controller.subscribe(() => setState(controller.state)), [controller]);

  function toggleAccordion() {
    setExpandMode((prev) => !prev);
  }

  if (!state.results.length) {
    return <div>No results</div>;
  }

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    productId: string,
    productType: string,
    title: string,
    edition: string,
    term?: string,
    year?: string,
    expand?: boolean,
    lob?: string
  ) => {
    setProductId(productId);
    setProductType(productType);
    setProductTitle(title);
    setEdition(edition);
    setYear(year || '');
    setTerm(term || '');
    setLob(lob || null);

    setExpandMode(expand || false);
    setOpenDrawer(true);
    setFieldSpecificConfig(getFieldSpecificConfig(productType));
  };

  const getFieldSpecificConfig = (productType: string) => {
    let fieldConfigData = getFieldSpecificConfiguration(productType, props.fieldConfig);
    const fieldSpecificConfiguration: IFieldSpecificConfig | undefined = fieldConfigData.map(
      (config) => {
        return {
          CartItemMaxQuantity: config.fields.CartItemMaxQuantity,
        };
      }
    )[0];
    return fieldSpecificConfiguration;
  };

  const handleClose = () => {
    setOpenDrawer(false);
  };

  function getRelatedPrograms(programs: Array<string> | any): Array<string> {
    if (programs && programs !== undefined) {
      return programs;
    }
    return [];
  }

  return (
    <div className="result-list">
      {state.results.map((result: any) => {
        const search_path =
          result.raw.isdatasheet === 'True'
            ? 'datasheets'
            : String(result.raw.producttype) === ProductType.StatisticsReport
            ? 'statisticsreports'
            : 'standards';
        const queryString =
          String(result.raw.producttype) === ProductType.StatisticsReport
            ? `?reportYear=${result.raw.reportyear}&term=${result.raw.term}`
            : result.raw.ismostrecent !== 'True'
            ? `?edition=${result.raw.edition}`
            : '';
        let to_navigate = `/${search_path}/${result.raw.productcode}${queryString}`;

        const productType = String(result.raw.producttype || '');
        const modality = (
          Array.isArray(result.raw.modality)
            ? result.raw.modality[0]
            : result.raw.modality || result.raw.courseModality || ''
        ).toString();
        const productId = result.raw.productcode;

        if (productType === TRAINING_IDENTIFIER_COVEO && productId) {
          if (modality === ProductType.InstructorLedCourse) {
            to_navigate = `/instructor-led-courses/${productId}`;
          } else if (modality === ProductType.SelfPacedCourse) {
            to_navigate = `/self-paced-courses/${productId}`;
          }
        }

        return result && result.raw ? (
          <div key={result.uniqueId} className="outer-box">
            <div className="box-frame">
              <div className="result-container">
                <div className="result-details-container">
                  <div className="result-header">
                    <InteractiveResult result={result} to_href={to_navigate}>
                      {result.title}
                      {addEllipsis(result.title)}
                    </InteractiveResult>
                  </div>
                  <div className="result-subheader">
                    {result.raw.producttype === TRAINING_IDENTIFIER_COVEO ? (
                      <div className="result-training" key={nextId()}>
                        <LocalLibraryOutlinedIcon className="result-training-icon" />
                        {result.raw.trainingtext}
                      </div>
                    ) : result.raw.ismostrecent === 'True' ? (
                      <div className="result-recent" key={nextId()}>
                        <CheckCircle className="result-recent-icon" />
                        {result.raw.mostrecenttext}
                      </div>
                    ) : (
                      <div className="result-previous" key={nextId()}>
                        <HistoryCircle className="result-recent-icon" />
                        {result.raw.historicaltext}
                      </div>
                    )}
                    {result?.raw?.edition && result.raw.edition !== '0' && (
                      <div className="result-edition" key={nextId()}>
                        <b>Edition:</b> {result.raw.edition}
                      </div>
                    )}
                    {result.raw.producttype !== TRAINING_IDENTIFIER_COVEO && (
                      <div className="result-status" key={nextId()}>
                        <b>Status: </b>
                        {result.raw.status}
                      </div>
                    )}
                    {result.raw.producttype === TRAINING_IDENTIFIER_COVEO &&
                      result.raw.language && (
                        <div className="result-status" key={nextId()}>
                          <b>Language: </b>
                          {Array.isArray(result.raw.language)
                            ? result.raw.language.join(', ')
                            : result.raw.language}
                        </div>
                      )}
                    {result.raw.producttype === TRAINING_IDENTIFIER_COVEO &&
                      result.raw.modality && (
                        <div className="result-status" key={nextId()}>
                          <b>Modality: </b>
                          {Array.isArray(result.raw.modality)
                            ? (result.raw.modality as string[]).map(translateProductType).join(', ')
                            : translateProductType(result.raw.modality as string)}
                        </div>
                      )}

                    {result.raw.reportyear && result.raw.term && (
                      <div className="result-status" key={nextId()}>
                        <b>Period: </b>
                        {result.raw.reporttype !== ReportType.Annual
                          ? `${formatTermDisplayValue(result.raw.term)}, ${result.raw.reportyear}`
                          : result.raw.reportyear}
                      </div>
                    )}
                  </div>

                  <div className="result-main" key={nextId()} title={'' + result.raw.description}>
                    <>
                      {result.raw.description && (
                        <span
                          className="ils-description"
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(result.raw.description as string),
                          }}
                        />
                      )}
                    </>
                  </div>

                  <div className="result-footer" key={nextId()}>
                    {result.raw.isaddendaerrataavailable === 'True' ? (
                      <Link
                        className="navbar-item"
                        to={`/${search_path}/${result.raw.productcode}`}
                        key={nextId()}
                      >
                        <> {result.raw.addendaerratatext}</>
                      </Link>
                    ) : (
                      ''
                    )}
                    {result.raw.ispreviousedition === 'True' ? (
                      <Link
                        className="navbar-item"
                        to={`/${search_path}/${result.raw.productcode}`}
                        key={nextId()}
                      >
                        <> {result.raw?.historicaleditionstext}</>
                      </Link>
                    ) : (
                      ''
                    )}
                    {String(result.raw.otherlanguagesavailable) === 'True' ? (
                      <Link
                        className="navbar-item"
                        to="#"
                        key={nextId()}
                        onClick={(event) =>
                          handleClick(
                            event,
                            String(result.raw.productcode),
                            String(result.raw.producttype),
                            String(result.title),
                            String(result.raw.edition),
                            String(result.raw.term),
                            String(result.raw.reportyear),
                            true,
                            String(result.raw.lob)
                          )
                        }
                      >
                        <> {result.raw.otherlanguagestext}</>
                      </Link>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
                <div
                  className={`result-cart-container ${
                    result.raw.lob === TRAINING_IDENTIFIER ? 'result-cart-training' : ''
                  }`}
                >
                  <div className="result-cart">
                    <div className="product-price">
                      <>{getCurrencyInUSD(result.raw.price)}</>
                    </div>
                    <div className="cart-button-div">
                      {(Array.isArray(result.raw.modality)
                        ? result.raw.modality
                        : result.raw.modality
                        ? [result.raw.modality]
                        : []
                      ).includes(ProductType.InstructorLedCourse) ? (
                        <InteractiveResult
                          result={result}
                          to_href={to_navigate}
                          className="btn-no-border-red"
                        >
                          REGISTER
                        </InteractiveResult>
                      ) : (
                        <Button
                          className="ws-white-button"
                          variant="contained"
                          key={nextId()}
                          onClick={(event: React.MouseEvent<HTMLElement>) =>
                            handleClick(
                              event,
                              String(result.raw.productcode),
                              String(result.raw.producttype),
                              String(result.title),
                              String(result.raw.edition),
                              String(result.raw.term),
                              String(result.raw.reportyear),
                              true,
                              String(result.raw.lob)
                            )
                          }
                        >
                          ADD TO CART
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {result.raw.programs && (
              <RelatedPrograms
                fields={{
                  Programs: props.Programs,
                  ProgramCount: props.ProgramCount,
                }}
                RelatedPrograms={getRelatedPrograms(result.raw.programs)}
                IsSearchResultsPage={true}
              ></RelatedPrograms>
            )}
          </div>
        ) : (
          <></>
        );
      })}
      <CartSlider
        productId={productId}
        productType={productType}
        productTitle={productTitle}
        edition={edition}
        openSlider={openDrawer}
        expandMode={expandMode}
        closeSlider={handleClose}
        toggleAccordion={toggleAccordion}
        loadPriceOptions={true}
        formatOptions={props.formatOptions}
        countryConfigFields={props.countryConfigFields}
        fieldSpecificConfig={fieldSpecificConfig}
        year={year}
        term={term}
        lob={lob ?? STANDARDS_IDENTIFIER}
      ></CartSlider>
    </div>
  );
};
export default ResultList;
